import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { base_url } from "./utils";

/**
 * Converts an image URL to a Base64 data URL.
 * Sets crossOrigin = "anonymous" before setting src.
 */
export const convertUrlToBase64 = async (url) => {
  if (!url || typeof url !== "string") return url;
  if (url.startsWith("data:") || url.startsWith("blob:")) return url;

  // 1. Check if backend email_invoice.php has base64 handler
  if (url.includes("/Sealsignimages/") || url.includes("/uploads/")) {
    try {
      const phpRes = await fetch(
        `${base_url}email_invoice.php?action=get_base64&url=${encodeURIComponent(url)}`
      );
      if (phpRes.ok) {
        const text = await phpRes.text();
        if (text && text.startsWith("data:image/")) {
          return text;
        }
      }
    } catch (e) {
      // Continue
    }
  }

  // 2. Direct fetch with FileReader
  try {
    const response = await fetch(url);
    if (response.ok) {
      const blob = await response.blob();
      return await new Promise((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = () => resolve(url);
        reader.readAsDataURL(blob);
      });
    }
  } catch (e) {
    // Direct fetch failed
  }

  // 3. Offscreen Image conversion (crossOrigin BEFORE src)
  try {
    const base64 = await new Promise((resolve) => {
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = () => {
        try {
          const canvas = document.createElement("canvas");
          canvas.width = img.naturalWidth || img.width || 200;
          canvas.height = img.naturalHeight || img.height || 200;
          const ctx = canvas.getContext("2d");
          ctx.drawImage(img, 0, 0);
          resolve(canvas.toDataURL("image/png"));
        } catch (err) {
          resolve(null);
        }
      };
      img.onerror = () => resolve(null);
      img.src = url;
    });

    if (base64) return base64;
  } catch (e) {
    // Continue
  }

  return url;
};

/**
 * Generates an A4 PDF Blob from a React DOM element / ref.
 * Guarantees images (QR code, Seal, Signature, Logos) are included by
 * cloning the node, setting crossOrigin before src, and converting to Base64.
 */
export const generatePdfFromElement = async (element) => {
  if (!element) return null;

  // 1. Create an offscreen clone so the live DOM is never modified or broken
  const clone = element.cloneNode(true);
  clone.style.position = "absolute";
  clone.style.left = "-9999px";
  clone.style.top = "0px";
  clone.style.width = element.offsetWidth ? `${element.offsetWidth}px` : "850px";
  clone.style.background = "#ffffff";
  clone.style.display = "block";
  clone.style.visibility = "visible";
  document.body.appendChild(clone);

  try {
    // 2. Set crossOrigin = "anonymous" before src and convert images to Base64
    const images = Array.from(clone.querySelectorAll("img"));
    await Promise.all(
      images.map(async (img) => {
        const originalSrc = img.src;
        if (originalSrc && !originalSrc.startsWith("data:")) {
          const base64 = await convertUrlToBase64(originalSrc);
          img.crossOrigin = "anonymous";
          img.src = base64 || originalSrc;
        } else {
          img.crossOrigin = "anonymous";
        }

        if (!img.complete || img.naturalWidth === 0) {
          await new Promise((resolve) => {
            img.onload = resolve;
            img.onerror = resolve;
            setTimeout(resolve, 500);
          });
        }
      })
    );

    // Wait 300ms for images to settle in cloned tree
    await new Promise((resolve) => setTimeout(resolve, 300));

    // 3. Render high-res canvas from the clone
    const canvas = await html2canvas(clone, {
      scale: 2,
      useCORS: true,
      allowTaint: true,
      backgroundColor: "#ffffff",
      logging: false,
    });

    // 4. Generate A4 PDF
    const imgData = canvas.toDataURL("image/jpeg", 0.95);
    const pdf = new jsPDF("p", "mm", "a4");
    const imgWidth = 210;
    const pageHeight = 297;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;
    let heightLeft = imgHeight;
    let position = 0;

    pdf.addImage(imgData, "JPEG", 0, position, imgWidth, imgHeight);
    heightLeft -= pageHeight;

    while (heightLeft > 0) {
      position = heightLeft - imgHeight;
      pdf.addPage();
      pdf.addImage(imgData, "JPEG", 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;
    }

    return pdf.output("blob");
  } finally {
    // Clean up clone
    if (clone && clone.parentNode) {
      clone.parentNode.removeChild(clone);
    }
  }
};
